import { useEffect, useRef, useState } from "react";
import { t } from "../i18n.ts";
import { getDetector, openCamera } from "./camera.ts";

/**
 * Видео задней камеры с рамкой прицела. Первый распознанный код уходит в
 * onCode один раз, камера гаснет вместе с компонентом.
 */
export function CameraView({ onCode }: { onCode: (code: string) => void }) {
  const video = useRef<HTMLVideoElement>(null);
  const [error, setError] = useState<string | null>(null);
  const done = useRef(false);
  const handler = useRef(onCode);

  useEffect(() => {
    handler.current = onCode;
  }, [onCode]);

  useEffect(() => {
    let stream: MediaStream | undefined;
    let timer: ReturnType<typeof setTimeout> | undefined;
    let alive = true;
    // Функцией, а не условием: камера гаснет посреди await, а сужение типа
    // в TypeScript об этом не знает.
    const stopped = () => !alive || done.current;

    const tick = async () => {
      const v = video.current;
      if (!v || stopped()) return;
      if (v.readyState >= 2) {
        try {
          const detector = await getDetector();
          const found = await detector.detect(v);
          const code = found[0]?.rawValue;
          if (code && !stopped()) {
            done.current = true;
            handler.current(code);
            return;
          }
        } catch {
          // Кадр не разобрался: следующий.
        }
      }
      // 120 мс между кадрами: телефон не греется, а скан ловится за доли секунды.
      timer = setTimeout(() => void tick(), 120);
    };

    openCamera()
      .then(async (s) => {
        if (!alive) {
          s.getTracks().forEach((tr) => tr.stop());
          return;
        }
        stream = s;
        const v = video.current;
        if (!v) return;
        v.srcObject = s;
        await v.play().catch(() => undefined);
        void tick();
      })
      .catch((e: unknown) => {
        setError(e instanceof DOMException && e.name === "NotAllowedError" ? t("camera.denied") : t("camera.unavailable"));
      });

    return () => {
      alive = false;
      if (timer) clearTimeout(timer);
      stream?.getTracks().forEach((tr) => tr.stop());
    };
  }, []);

  return (
    <div className="camera">
      {error ? (
        <p className="camera-error" role="alert">
          {error}
        </p>
      ) : (
        <>
          <video ref={video} className="camera-video" playsInline muted aria-label={t("camera.label")} />
          <span className="camera-aim" aria-hidden="true" />
        </>
      )}
    </div>
  );
}
